import React, { useState, useRef } from "react";
import {
  Dialog,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Button,
  Grid,
  Typography,
  Box,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import ReCAPTCHA from "react-google-recaptcha";
import { POST_CONTACT_US } from "../../../../api/url";
import { regionConfigs } from "../../../../config/regions";
import { toast } from "react-toastify";

const ContactFormModal = ({ open, handleClose }) => {
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down("sm"));
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    phone: "",
    subject: "",
    message: "",
    contactMethod: "email",
    Businessname: "",
  });

  const [errors, setErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [recaptchaValue, setRecaptchaValue] = useState(null);
  const recaptchaRef = useRef(null);

  const validateForm = () => {
    const newErrors = {};
    if (!formData.firstName.trim())
      newErrors.firstName = "First name is required";
    if (!formData.lastName.trim()) newErrors.lastName = "Last name is required";
    if (!formData.Businessname.trim())
      newErrors.Businessname = "Business name is required";
    if (!formData.email.trim()) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Email is invalid";
    }
    if (!formData.phone.trim()) newErrors.phone = "Phone number is required";
    if (!formData.subject.trim()) newErrors.subject = "Subject is required";
    if (!formData.message.trim()) newErrors.message = "Message is required";
    if (!recaptchaValue)
      newErrors.recaptcha = "Please complete the reCAPTCHA verification";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setIsSubmitting(true);

    try {
      const htmlForm = `
        <div style="font-size:16px">
          <table>
            <tr><td><b>Subject</b></td><td>${formData.subject}</td></tr>
            <tr><td><b>Name</b></td><td>${formData.firstName} ${formData.lastName}</td></tr>
            <tr><td><b>Email</b></td><td>${formData.email}</td></tr>
            <tr><td><b>Business</b></td><td>${formData.Businessname}</td></tr>
            <tr><td><b>Phone</b></td><td>${formData.phone}</td></tr>
            <tr><td><b>Message</b></td><td>${formData.message}</td></tr>
          </table>
        </div>
      `;

      const applicationId = regionConfigs.nz.applicationId;

      const payload = {
        HtmlContent: htmlForm,
        ApplicationId: applicationId,
        subject: "Contact Us form submitted on Tuatara Website",
        toEmail: formData.email,
        ReCaptchaResponse: recaptchaValue,
      };

      const response = await fetch(POST_CONTACT_US, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          "Application-Id": applicationId,
        },
        body: JSON.stringify(payload),
      });

      if (!response.ok) throw new Error(`Error: ${response.status}`);

      const result = await response.text();
      console.log("API result:", result);

      toast.success(result);
      handleCloseModal();
    } catch (error) {
      console.error("Error submitting form:", error);
      toast.error(
        "There was an error submitting your message. Please try again."
      );
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCloseModal = () => {
    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      phone: "",
      subject: "",
      message: "",
      contactMethod: "email",
      Businessname: "",
    });
    setErrors({});
    setRecaptchaValue(null);
    if (recaptchaRef.current) recaptchaRef.current.reset();
    handleClose();
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => ({ ...prev, [name]: "" }));
  };

  return (
    <Dialog open={open} onClose={handleCloseModal} fullWidth maxWidth="md" fullScreen={fullScreen}>
      {/* Header */}
      <DialogTitle
        sx={{
          backgroundColor: "#77C15F",
          color: "white",
          fontWeight: "bold",
          textAlign: "left",
          fontSize: "1.5rem",
        }}
      >
        Contact Us
        <IconButton
          aria-label="close"
          onClick={handleCloseModal}
          sx={{ position: "absolute", right: 8, top: 8, color: "white" }}
        >
          <Close />
        </IconButton>
      </DialogTitle>

      {/* Content */}
      <DialogContent dividers sx={{ p: { xs: 2, sm: 3 } }}>
        <form onSubmit={handleSubmit}>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="First Name"
                name="firstName"
                value={formData.firstName}
                onChange={handleInputChange}
                error={!!errors.firstName}
                helperText={errors.firstName}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Last Name"
                name="lastName"
                value={formData.lastName}
                onChange={handleInputChange}
                error={!!errors.lastName}
                helperText={errors.lastName}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Business Name"
                name="Businessname"
                value={formData.Businessname}
                onChange={handleInputChange}
                error={!!errors.Businessname}
                helperText={errors.Businessname}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Email"
                name="email"
                value={formData.email}
                onChange={handleInputChange}
                error={!!errors.email}
                helperText={errors.email}
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Phone"
                name="phone"
                value={formData.phone}
                onChange={handleInputChange}
                error={!!errors.phone}
                helperText={errors.phone}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Subject"
                name="subject"
                value={formData.subject}
                onChange={handleInputChange}
                error={!!errors.subject}
                helperText={errors.subject}
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Message"
                name="message"
                multiline
                rows={4}
                value={formData.message}
                onChange={handleInputChange}
                error={!!errors.message}
                helperText={errors.message}
              />
            </Grid>
            {/* reCAPTCHA + Submit Button */}
            <Grid
              container
              spacing={2}
              mt={2}
              m="1px"
              justifyContent="space-between"
              alignItems="center"
            >
              <Grid item xs={12} sm={6} textAlign="left">
                <Box
                  sx={{
                    display: "inline-block",
                    transformOrigin: "left center",
                    transform: fullScreen ? "scale(0.85)" : "scale(1)",
                    "@media (max-width: 360px)": { transform: "scale(0.72)" },
                  }}
                >
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={"6LdhUSMqAAAAANByfdbSyhgGZS1-SeDIVDPAq7rb"}
                    onChange={(value) => setRecaptchaValue(value)}
                  />
                </Box>
                {errors.recaptcha && (
                  <Typography variant="body2" color="error" mt={0.5}>
                    {errors.recaptcha}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={12} sm={6} textAlign={{ xs: "center", sm: "end" }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={isSubmitting}
                  sx={{
                    backgroundColor: "#77C15F",
                    px: 4,
                    py: 1,
                    fontSize: "1rem",
                    borderRadius: "5px",
                    textTransform: "uppercase",
                    width: { xs: "100%", sm: "auto" },
                    "&:hover": { backgroundColor: "#57ad3c" },
                  }}
                >
                  {isSubmitting ? "Sending..." : "Send"}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default ContactFormModal;
